import React, { useState, useEffect } from 'react';
import { Box, Typography, Button } from '@mui/material';
import { motion } from 'framer-motion';
import WordListLayout from '../../../../shared/reusables/WordListLayout';
import { fetchWordsByCategory } from '../../../../shared/api/darija';

/**
 * NounsWordList — word list for nouns with category selection.
 * Kept separate from GenericWordList because nouns are grouped by category.
 */
export default function NounsWordList({ onBack }) {
    const [nounsData, setNounsData] = useState(null);
    const [selectedCategory, setSelectedCategory] = useState(null);
    const [error, setError] = useState(null);

    useEffect(() => {
        fetchWordsByCategory('nouns')
            .then(setNounsData)
            .catch((err) => {
                console.error('[NounsWordList]:', err);
                setError(err.message || 'Ophalen mislukt.');
            });
    }, []);

    if (error) {
        return (
            <WordListLayout
                title="Zelfstandige naamwoorden"
                subtitle="Fout bij laden"
                accentColor="#14b8b8"
                accentColor2="#0e8c8c"
                label="Woordenlijst"
                onBack={onBack}
            >
                <Typography sx={{ color: '#f87171', fontFamily: "'DM Sans', sans-serif", textAlign: 'center', mt: 4 }}>
                    ⚠️ {error}
                </Typography>
            </WordListLayout>
        );
    }

    if (!nounsData) {
        return (
            <WordListLayout
                title="Zelfstandige naamwoorden"
                subtitle="Laden…"
                accentColor="#14b8b8"
                accentColor2="#0e8c8c"
                label="Woordenlijst"
                onBack={onBack}
            >
                <Box />
            </WordListLayout>
        );
    }

    const categories = Object.keys(nounsData);

    // ── Category picker ──────────────────────────────────────────────────────
    if (!selectedCategory) {
        return (
            <WordListLayout
                title="Zelfstandige naamwoorden"
                subtitle={`${categories.length} categorieën`}
                accentColor="#14b8b8"
                accentColor2="#0e8c8c"
                label="Woordenlijst"
                onBack={onBack}
            >
                <Box
                    sx={{
                        display: 'grid',
                        gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: 'repeat(3, 1fr)' },
                        gap: 2,
                    }}
                >
                    {categories.map((cat, i) => (
                        <motion.div
                            key={cat}
                            initial={{ opacity: 0, y: 16 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: i * 0.04, duration: 0.28 }}
                        >
                            <Button
                                fullWidth
                                onClick={() => setSelectedCategory(cat)}
                                sx={{
                                    height: { xs: 72, sm: 86 },
                                    borderRadius: '14px',
                                    flexDirection: 'column',
                                    textTransform: 'none',
                                    fontFamily: "'DM Sans', sans-serif",
                                    fontWeight: 600,
                                    fontSize: '0.95rem',
                                    background: 'rgba(255,255,255,0.04)',
                                    border: '1px solid rgba(255,255,255,0.08)',
                                    color: '#e8e8f0',
                                    transition: 'all 0.18s ease',
                                    gap: 0.5,
                                    '&:hover': {
                                        background: 'rgba(20,184,184,0.1)',
                                        borderColor: 'rgba(20,184,184,0.4)',
                                        color: '#5ee0e0',
                                        transform: 'translateY(-2px)',
                                    },
                                }}
                            >
                                {cat}
                                <Typography
                                    component="span"
                                    sx={{ fontSize: '0.72rem', color: '#6060a0', fontWeight: 400 }}
                                >
                                    {(nounsData[cat] ?? []).length} woorden
                                </Typography>
                            </Button>
                        </motion.div>
                    ))}
                </Box>
            </WordListLayout>
        );
    }

    // ── Word list for selected category ──────────────────────────────────────
    const words = [...(nounsData[selectedCategory] ?? [])].sort((a, b) =>
        a.dutch.localeCompare(b.dutch, 'nl')
    );

    return (
        <WordListLayout
            title={selectedCategory}
            subtitle={`${words.length} woorden · Zelfstandige naamwoorden`}
            accentColor="#14b8b8"
            accentColor2="#0e8c8c"
            label="Woordenlijst"
            onBack={() => setSelectedCategory(null)}
        >
            <Box
                sx={{
                    background: 'rgba(255,255,255,0.03)',
                    backdropFilter: 'blur(12px)',
                    border: '1px solid rgba(255,255,255,0.08)',
                    borderRadius: '16px',
                    overflow: 'hidden',
                }}
            >
                <Box
                    sx={{
                        display: 'grid',
                        gridTemplateColumns: '1fr 1fr',
                        gap: 2,
                        px: { xs: 2, sm: 3 },
                        py: 1.2,
                        borderBottom: '1px solid rgba(255,255,255,0.08)',
                        background: 'rgba(255,255,255,0.03)',
                    }}
                >
                    {['Nederlands', 'Darija'].map((col) => (
                        <Typography
                            key={col}
                            sx={{
                                color: '#7878a0',
                                fontFamily: "'DM Sans', sans-serif",
                                fontSize: '0.72rem',
                                fontWeight: 700,
                                textTransform: 'uppercase',
                                letterSpacing: '0.1em',
                            }}
                        >
                            {col}
                        </Typography>
                    ))}
                </Box>

                {words.map((item, idx) => (
                    <motion.div
                        key={item.dutch}
                        initial={{ opacity: 0, x: -8 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: Math.min(idx * 0.01, 0.35), duration: 0.22 }}
                    >
                        <Box
                            sx={{
                                display: 'grid',
                                gridTemplateColumns: '1fr 1fr',
                                gap: 2,
                                px: { xs: 2, sm: 3 },
                                py: 1.5,
                                borderBottom: idx < words.length - 1 ? '1px solid rgba(255,255,255,0.04)' : 'none',
                                '&:hover': { background: 'rgba(20,184,184,0.05)' },
                                transition: 'background 0.15s',
                            }}
                        >
                            <Typography sx={{ color: '#e8e8f0', fontFamily: "'DM Sans', sans-serif", fontWeight: 600, fontSize: '0.93rem' }}>
                                {item.dutch}
                            </Typography>
                            <Typography sx={{ color: '#14b8b8', fontFamily: "'DM Sans', sans-serif", fontSize: '0.93rem', fontWeight: 500 }}>
                                {item.darija}
                            </Typography>
                        </Box>
                    </motion.div>
                ))}
            </Box>
        </WordListLayout>
    );
}
